// src/pages/AdminPriceSettings.jsx
import React, { useState, useEffect } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, 
  ShoppingCart, 
  Package, 
  Save, 
  ArrowLeft,
  Shield,
  AlertCircle
} from 'lucide-react';
import Swal from 'sweetalert2';

const showAlert = (message, type) => {
  Swal.fire({
    text: message,
    icon: type,
    toast: true,
    position: 'top',
    timer: 3000,
    timerProgressBar: true,
    showConfirmButton: false,
  });
};

export default function AdminPriceSettings() {
  const [settings, setSettings] = useState({
    buy_enabled: true,
    sell_enabled: true,
    buy_start_time: '00:00',
    buy_end_time: '23:59',
    sell_start_time: '00:00',
    sell_end_time: '23:59',
    min_weight: 0.001,
    max_weight: 1000
  });
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  // ریسپانسیو
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // رفرش توکن
  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');
      const res = await api.post('/api/auth/refresh-token', { refreshToken });
      localStorage.setItem('authToken', res.data.accessToken);
      return res.data.accessToken;
    } catch (err) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      navigate('/login_page');
      return null;
    }
  };

  // دریافت تنظیمات
  useEffect(() => {
    if (!token) {
      navigate('/login_page');
      return;
    }

    const fetchSettings = async () => {
      try {
        const newToken = token || (await refreshAccessToken());
        const res = await api.get('/api/priceSettings', {
          headers: { Authorization: `Bearer ${newToken}` }
        });
        setSettings(res.data);
      } catch (err) {
        showAlert('خطا در دریافت تنظیمات', 'error');
      }
    };

    fetchSettings();
  }, [token, navigate]);

  // ذخیره تنظیمات
  const handleSave = async () => {
    setLoading(true);
    try {
      const newToken = token || (await refreshAccessToken());
      await api.put('/api/priceSettings', settings, {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      showAlert('تنظیمات با موفقیت ذخیره شد', 'success');
    } catch (err) {
      showAlert(err.response?.data?.message || 'خطا در ذخیره', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 px-4 py-8 font-vazir">
      <div className="max-w-5xl mx-auto">
        {/* برگشت */}
        <motion.button
          whileHover={{ x: -5 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-indigo-600 mb-6 text-sm font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          برگشت
        </motion.button>

        {/* هدر */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center text-white shadow-xl">
              <Shield className="w-9 h-9" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-800">تنظیمات زمان‌بندی خرید و فروش</h2>
          <p className="text-gray-600 mt-2">مدیریت ساعات کاری و محدودیت‌های معامله</p>
        </motion.div>

        {/* کارت اصلی */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-6 md:p-8 border border-white/30"
        >
          <div className="space-y-8">

            {/* خرید */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg">
                  <ShoppingCart className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-blue-700">تنظیمات خرید</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    ساعت شروع خرید
                  </label>
                  <input
                    type="time"
                    value={settings.buy_start_time}
                    onChange={(e) => setSettings({ ...settings, buy_start_time: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-blue-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    ساعت پایان خرید
                  </label>
                  <input
                    type="time"
                    value={settings.buy_end_time}
                    onChange={(e) => setSettings({ ...settings, buy_end_time: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-blue-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.buy_enabled}
                    onChange={(e) => setSettings({ ...settings, buy_enabled: e.target.checked })}
                    className="w-6 h-6 text-blue-600 rounded-lg focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-gray-700 font-medium">فعال بودن خرید</span>
                </label>
              </div>
            </motion.div>

            {/* فروش */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-6 border border-yellow-100"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-2xl flex items-center justify-center text-white shadow-lg">
                  <Package className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-yellow-700">تنظیمات فروش</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4 text-yellow-600" />
                    ساعت شروع فروش
                  </label>
                  <input
                    type="time"
                    value={settings.sell_start_time}
                    onChange={(e) => setSettings({ ...settings, sell_start_time: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-yellow-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-300"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4 text-yellow-600" />
                    ساعت پایان فروش
                  </label>
                  <input
                    type="time"
                    value={settings.sell_end_time}
                    onChange={(e) => setSettings({ ...settings, sell_end_time: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-yellow-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-300"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.sell_enabled}
                    onChange={(e) => setSettings({ ...settings, sell_enabled: e.target.checked })}
                    className="w-6 h-6 text-yellow-600 rounded-lg focus:ring-yellow-500 focus:ring-2"
                  />
                  <span className="text-gray-700 font-medium">فعال بودن فروش</span>
                </label>
              </div>
            </motion.div>

            {/* محدودیت وزن */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center text-white shadow-lg">
                  <AlertCircle className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-purple-700">محدودیت وزن معامله</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    حداقل وزن (گرم)
                  </label>
                  <input
                    type="number"
                    step="0.001"
                    min="0.001"
                    value={settings.min_weight}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0.001;
                      if (val <= settings.max_weight) {
                        setSettings({ ...settings, min_weight: val });
                      }
                    }}
                    className="w-full px-4 py-3 bg-white border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                  />
                  <p className="text-xs text-purple-600 mt-2 flex items-center gap-1">
                    مثال: 0.001 = 1 سوت
                  </p>
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    حداکثر وزن (گرم)
                  </label>
                  <input
                    type="number"
                    step="0.001"
                    min={settings.min_weight}
                    value={settings.max_weight}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 1000;
                      if (val >= settings.min_weight) {
                        setSettings({ ...settings, max_weight: val });
                      }
                    }}
                    className="w-full px-4 py-3 bg-white border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                  />
                  <p className="text-xs text-purple-600 mt-2 flex items-center gap-1">
                    مثال: 1000 = 1 کیلوگرم
                  </p>
                </div>
              </div>
            </motion.div>

            {/* دکمه ذخیره */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex justify-center mt-8"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSave}
                disabled={loading}
                className={`flex items-center gap-3 px-10 py-4 rounded-2xl font-bold text-white shadow-xl transition-all duration-300 ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
                }`}
              >
                <Save className="w-6 h-6" />
                {loading ? 'در حال ذخیره...' : 'ذخیره تنظیمات'}
              </motion.button>
            </motion.div>
          </div>
        </motion.div>

        {/* فوتر */}
        <div className="text-center mt-10 text-xs text-gray-500">
          <p>© ۱۴۰۴ - ESF.Gold | پنل مدیریت</p>
        </div>
      </div>
    </div>
  );
}